import * as React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { AuthContext } from "@/contexts/authContext";
import { parrainageService } from "@/services/parrainageService";
import { useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { depositService } from "@/services/depositService";

const ParrainagePage = () => {
  const { user } = useContext(AuthContext);
  const numero_commercial = user?.phone_number;
  const [parrainages, setParrainages] = useState([]);
  const [depots, setDepots] = useState([]);
  const [clientId, setClientId] = useState("");
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const columns = [
    { accessorKey: "client_id", header: "Client ID", cell: ({ row }: { row: any }) => <div>{row.getValue("client_id")}</div> },
    { accessorKey: "numero_commercial", header: "N° Commercial", cell: ({ row }: { row: any }) => <div>{row.getValue("numero_commercial")}</div> },
    { accessorKey: "date_created", header: ({ column }: { column: any }) => (<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>Date <span className="ml-2">↕</span></Button>), cell: ({ row }: { row: any }) => <div>{row.getValue("date_created")?.slice(0, 10) ?? "-"}</div> },
    {
      id: "validated",
      header: "Validé (Depot lors des 30 derniers jours)",
      cell: ({ row }: { row: any }) => {
        const clientId = row.getValue("client_id");
        // depots list from upper scope
        const isValidated = depots.some((dep: any) => dep.nom === clientId || dep.account_id === clientId);
        return <span className={"px-2 py-1 rounded text-xs font-bold " + (isValidated ? "text-green-600" : "text-red-400")}>{isValidated ? "oui" : "non"}</span>;
      }
    },
  ];

  useEffect(() => {
    const fetch = async () => {
      if (numero_commercial) {
        setLoading(true);
        const [parrainagesResult, depotsResult] = await Promise.all([
          parrainageService.fetchParrainageList(numero_commercial),
          depositService.fetchDepositHistory(numero_commercial)
        ]);
        setParrainages(parrainagesResult?.parrainages as any ?? []);
        setDepots(depotsResult?.depots as any ?? []);
        setLoading(false);
      }
    };
    fetch();
  }, [numero_commercial]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!numero_commercial || !clientId) return;
    setLoading(true);
    const result = await parrainageService.addParrainage({ numero_commercial, client_id: clientId });
    if (result.success) {
      const next = await parrainageService.fetchParrainageList(numero_commercial);
      setParrainages(next?.parrainages as any ?? []);
      setClientId("");
      setLoading(false);
      toast.success("Parrainage ajouté avec succès");
    } else {
      setLoading(false);
      toast.error(result.error?.message ?? "Erreur lors de l'ajout du parrainage");
    }
  };

  const table = useReactTable({
    data: parrainages,
    columns,
    state: { sorting, columnFilters, columnVisibility, rowSelection },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    initialState: {
      pagination: { pageSize: 50 },
    },
  });

  return (
    <div className="w-full max-w-2xl mx-auto py-10 px-4">
      {/* Header card */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 flex flex-col items-center text-center border border-blue-100">
        <div className="bg-blue-500 text-white rounded-full p-3 mb-4">
          <Users className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-semibold mb-2 text-blue-700">Parrainage</h2>
        <p className="mb-4 text-gray-600">Vous pouvez parrainer manuellement un utilisateur en renseignant son identifiant client ci-dessous.<br/>Tous vos filleuls sont listés dans le tableau ci-dessous.</p>
        <form onSubmit={handleAdd} className="flex flex-col md:flex-row gap-2 w-full max-w-md mx-auto">
          <Input
            placeholder="Entrer l'identifiant client à parrainer..."
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            required
            className="flex-grow"
          />
          <Button type="submit" disabled={loading || !clientId} className="bg-blue-600 shadow hover:bg-blue-700 transition-all duration-150">
            Parrainer &nbsp; <Users className="w-5 h-5 inline" />
          </Button>
        </form>
      </div>

      {/* Filtre */}
      <div className="flex items-center mb-4">
        <Input
          placeholder="Filtrer par Client ID..."
          value={(table.getColumn("client_id")?.getFilterValue() as string) ?? ""}
          onChange={(e) =>
            table.getColumn("client_id")?.setFilterValue(e.target.value)
          }
          className="w-full"
        />
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-white shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="text-blue-700 bg-blue-50">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center text-gray-400">
                    Aucun utilisateur parrainé pour l'instant.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default ParrainagePage;
